import { cx } from '@flexbyt/widget-preview';
import { useEffect, useRef, useState } from 'react';

/** The legacy limit, and the one the copy promises. */
export const MAX_PHOTOS = 5;

/**
 * Pictures of the thing being reviewed.
 *
 * The legacy form used rsuite's `Uploader`, which is a dependency this site
 * does not have and does not want for one control. This is a plain
 * `<input type="file">` with its own previews.
 *
 * ---------------------------------------------------------------------------
 * Object URLs are revoked
 * ---------------------------------------------------------------------------
 * Every preview holds a blob in memory until its URL is released. The legacy
 * control never released them, which on a phone — where somebody adds five
 * photos, removes three and adds three more — is exactly the page that runs out
 * of memory mid-review. The URLs are created alongside the files and revoked
 * when the file goes or the component unmounts.
 */
export function PhotoPicker({
  photos,
  onChange,
  label,
  hint,
}: {
  photos: File[];
  onChange: (photos: File[]) => void;
  label: string;
  hint: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>([]);

  useEffect(() => {
    const urls = photos.map((photo) => URL.createObjectURL(photo));
    setPreviews(urls);
    return () => {
      for (const url of urls) URL.revokeObjectURL(url);
    };
  }, [photos]);

  const full = photos.length >= MAX_PHOTOS;

  function add(chosen: FileList | null) {
    if (!chosen) return;
    // Trimmed rather than rejected. Somebody who picks eight photos meant to
    // add photos; refusing all eight over a limit they were told about in
    // passing is a worse answer than taking the first five.
    onChange([...photos, ...Array.from(chosen)].slice(0, MAX_PHOTOS));
    // Cleared so re-picking the same file fires `change` again.
    if (input.current) input.current.value = '';
  }

  return (
    <div className="mt-5">
      <p className="text-caption font-semibold text-ink-900">{label}</p>
      <p className="mt-1 text-micro text-ink-500">{hint}</p>

      <ul className="mt-2 flex flex-wrap gap-2">
        {photos.map((photo, index) => (
          <li key={`${photo.name}-${index}`} className="relative">
            <img
              src={previews[index]}
              alt=""
              className="size-20 rounded-xl object-cover shadow-sm"
            />
            <button
              type="button"
              onClick={() => onChange(photos.filter((_, at) => at !== index))}
              className={cx(
                'absolute -top-1.5 -right-1.5 grid size-5 place-items-center rounded-full',
                'bg-ink-950 text-[11px] leading-none text-white',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600'
              )}
            >
              <span aria-hidden>×</span>
              <span className="sr-only">Remove photo {index + 1}</span>
            </button>
          </li>
        ))}

        {!full && (
          <li>
            <button
              type="button"
              onClick={() => input.current?.click()}
              className={cx(
                // The same soft fill as the fields, and their brand-colour ring
                // on hover: an obvious, friendly place to tap.
                'grid size-20 place-items-center rounded-xl bg-[#f3f3f5] text-2xl text-ink-500',
                'transition-[background-color,box-shadow,transform] duration-[var(--duration-fast)]',
                'hover:-translate-y-px hover:bg-white hover:text-ink-900',
                'hover:shadow-[0_0_0_3px_var(--rj-brand)]',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-600'
              )}
            >
              <span aria-hidden>+</span>
              <span className="sr-only">{label}</span>
            </button>
          </li>
        )}
      </ul>

      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        className="sr-only"
        tabIndex={-1}
        onChange={(event) => add(event.target.files)}
      />
    </div>
  );
}
